import { Tabs } from "@circleco/compass/components/Tabs";
import { useNavigate } from "@tanstack/react-router";
import { useState, useCallback } from "react";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { PendingConnections } from "@/react/components/MemberDirectory/AllMembers/PendingConnections";
import { Recommendations } from "@/react/components/MemberDirectory/AllMembers/Recommendations";
import { MyConnections } from "@/react/components/MemberDirectory/Connections/MyConnections";
import { MemberCountProvider } from "@/react/components/MemberDirectory/shared/MobileFilters/MemberCountContext";
import { useMemberDirectorySettings } from "@/react/components/MemberDirectory/useMemberDirectorySettings";
import { EMPTY_MEMBER_IMAGES } from "@/react/components/shared/MembersList/CommunityMembers/constants";
import { ProgressiveImage } from "@/react/components/shared/ProgressiveImage";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { isMemberDirectoryVisible } from "@/react/helpers/communityMemberHelpers";
import { invalidateConnectionRequestsQueryCache } from "@/react/hooks/connectionRequests/connectionRequestCacheHelpers";
import {
  useConnectionRequestWebSocket,
  type ConnectionRequestWebSocketEvent,
} from "@/react/hooks/connectionRequests/useConnectionRequestWebSocket";
import { useRecommendationsInfiniteQuery } from "@/react/hooks/recommendations/useRecommendationsInfiniteQuery";
import { useIsConnectRecommendationsEnabled } from "@/react/hooks/useIsConnectEnabled";
import { usePendingConnections } from "@/react/hooks/usePendingConnections";
import { useThemeContext } from "@/react/providers/Theme";

import {
  CONNECT_PANEL_SCROLL_CONTAINER_ID,
  TAB_DISCOVER,
  TAB_MY_CONNECTIONS,
  TABS,
} from "./constants";
import { DiscoverEmptyState } from "./DiscoverEmptyState";

interface BodyProps {
  onClose?: () => void;
}

export const Body = ({ onClose }: BodyProps) => {
  const navigate = useNavigate();
  const { currentAppearance } = useThemeContext();
  const memberDirectorySettings = useMemberDirectorySettings();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(TAB_DISCOVER);

  const { currentCommunityMember, currentCommunitySettings } =
    usePunditUserContext();

  const shouldShowCtaOnEmptyState = isMemberDirectoryVisible(
    currentCommunityMember,
    currentCommunitySettings,
  );

  const pendingConnectionsQuery = usePendingConnections({
    perPage: 2,
  });

  const isConnectRecommendationsEnabled = useIsConnectRecommendationsEnabled();
  const shouldFetchRecommendations = Boolean(
    isConnectRecommendationsEnabled && currentCommunityMember,
  );

  const recommendationsQuery = useRecommendationsInfiniteQuery({
    perPage: 2,
    scope: "connect-tab-header",
    options: {
      enabled: shouldFetchRecommendations,
    },
  });

  const { refetch: refetchPending } = pendingConnectionsQuery;
  const { refetch: refetchRecommendations } = recommendationsQuery;

  const handleWebSocketMessage = useCallback(
    (eventData: ConnectionRequestWebSocketEvent) => {
      if (eventData.event === "updatePendingRequestsCount") {
        invalidateConnectionRequestsQueryCache({ queryClient });
        void refetchPending();
      } else if (eventData.event === "connectionStatusChanged") {
        invalidateConnectionRequestsQueryCache({
          queryClient,
          initiatorPublicUid: eventData.initiator_public_uid,
          recipientPublicUid: eventData.recipient_public_uid,
        });
        void refetchPending();
        void refetchRecommendations();
      }
    },
    [queryClient, refetchPending, refetchRecommendations],
  );

  useConnectionRequestWebSocket(
    currentCommunityMember?.id,
    handleWebSocketMessage,
  );

  const shouldShowEmptyState =
    pendingConnectionsQuery.audience.length === 0 &&
    recommendationsQuery.recommendations.length === 0 &&
    !pendingConnectionsQuery.isLoading &&
    !recommendationsQuery.isLoading;

  const emptyMembersImageVariants = EMPTY_MEMBER_IMAGES[currentAppearance];

  return (
    <div className="bg-primary flex h-full flex-col overflow-hidden">
      <div className="shrink-0 border-b border-primary px-6 pb-4 pt-2 lg:pt-0">
        <Tabs.Root
          tabs={TABS}
          selectedValue={selectedTab}
          onValueChange={setSelectedTab}
          size="md"
        />
      </div>
      <div
        data-testid="tab-panels"
        className="flex flex-1 flex-col overflow-hidden pb-10"
      >
        {selectedTab === TAB_DISCOVER && (
          <div
            data-testid="connect-panel-tab-discover"
            className="flex-1 overflow-y-auto"
          >
            <div
              className="flex flex-col gap-4 p-4"
              data-testid="connect-tab-discover"
            >
              <PendingConnections
                pendingConnectionsQuery={pendingConnectionsQuery}
              />
              {isConnectRecommendationsEnabled && (
                <Recommendations recommendationsQuery={recommendationsQuery} />
              )}
              {shouldShowEmptyState && (
                <div className="flex flex-col items-center justify-center">
                  <div className="mt-14 flex flex-1 items-center justify-center">
                    <ProgressiveImage
                      className="h-36"
                      src={emptyMembersImageVariants.large}
                      srcVariants={emptyMembersImageVariants}
                      alt={t("members_directory.no_members.title")}
                      imageClassName="object-contain"
                    />
                  </div>
                  <DiscoverEmptyState
                    header={t(
                      "header_v3.connect.tabs.discover.empty_state.title",
                    )}
                    description={t(
                      `header_v3.connect.tabs.discover.empty_state.${shouldShowCtaOnEmptyState ? "description_with_cta" : "description"}`,
                    )}
                    secondaryLabel={
                      shouldShowCtaOnEmptyState
                        ? t("header_v3.connect.tabs.discover.empty_state.cta")
                        : undefined
                    }
                    onSecondaryClick={() => {
                      void navigate({ to: "/members" });
                      onClose?.();
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {selectedTab === TAB_MY_CONNECTIONS && (
          <div
            id={CONNECT_PANEL_SCROLL_CONTAINER_ID}
            data-testid="tab-panel"
            className="flex-1 overflow-y-auto"
          >
            <div
              className="flex flex-col gap-4 p-4"
              data-testid="connect-tab-my-connections"
            >
              <MemberCountProvider>
                <MyConnections
                  memberDirectorySettings={memberDirectorySettings}
                />
              </MemberCountProvider>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
